/**
 * Die Angaben, die **nur die Rechtstexte der Anwendung** tragen — Fassung der
 * Nutzungsbedingungen, Stand und die Fristen der Datenschutzerklärung.
 *
 * Bis HM-158 lagen sie in `apps/web/src/lib/legal.ts`. Seit die Mobile-App
 * dieselben Texte selbst anzeigt (statt die Web-Seite im Browser zu öffnen),
 * lesen zwei Apps sie, und Apps importieren sich nicht gegenseitig. Die
 * Webseite hausmeter.de nennt eigene Angaben und liest diese nicht.
 *
 * **Die Fristen bleiben an ihren Test gebunden:** `apps/web/src/lib/legal.test.ts`
 * hält jede gegen `@hausmeter/domain`, `@hausmeter/timeseries` und
 * `.env.example`. Dieses Paket importiert nichts — auch nicht für die Prüfung.
 */
import { CONTACT, EU_ONLY_CONFIRMED, PROCESSORS_COMPLETE } from "./provider.js";

/**
 * Die Fassung, der bei der Registrierung zugestimmt wird — und die Fassung, die
 * unter `/nutzungsbedingungen` steht. **Dieselbe Konstante für beides**, denn
 * genau das war der Mangel, den OQ-033 beschrieb: `acceptedTermsVersion` hielt
 * seit HM-002 eine Zustimmung zu `2026-01` fest, und einen Text dieser Fassung
 * gab es nie.
 *
 * `2026-08` war die erste Fassung, die es wirklich gibt. Die vorher
 * aufgezeichneten Zustimmungen bleiben stehen und bezeichnen weiterhin nichts;
 * sie nachträglich umzuschreiben hieße, eine Erklärung zu erfinden, die niemand
 * abgegeben hat.
 *
 * **`2026-09` ergänzt § 3 um das Bewohnerkonto** (HM-116). Jede Änderung am
 * Text ist eine neue Nummer, auch eine beschreibende: Sonst hielte eine
 * Zustimmung zu `2026-08` fest, einem Text zugestimmt zu haben, der so nie
 * dastand. Der Wortlaut von `2026-08` liegt im Tag `v0.32.0`. Ausgerollt ohne
 * Mitteilung nach § 10, weil es nur Testkonten des Betreibers gab (**OQ-052**);
 * die nächste Fassung braucht diesen Weg.
 */
export const TERMS_VERSION = "2026-09";

/**
 * Der Stand der Texte. Er steht auf jeder der drei Seiten und ist bewusst
 * **grob** — Monat und Jahr: Ein tagesgenaues Datum verspricht eine Pflege, die
 * niemand leistet, und ein Text, der seit acht Monaten unverändert richtig ist,
 * ist nicht veraltet.
 */
export const LEGAL_AS_OF = "November 2026";

/* --------------------------------------------------------------------------
 * Fristen — als Zahl hier, als Zusicherung im Test daneben
 * ----------------------------------------------------------------------- */

/**
 * **Warum die Zahlen hier stehen und nicht importiert werden.**
 *
 * Sie stammen aus `@hausmeter/domain` und `@hausmeter/timeseries`, und der
 * naheliegende Weg wäre, sie von dort zu holen. Er ist versperrt: Die Web-App darf
 * `@hausmeter/domain` nur für **Typen** benutzen (`apps/web/CLAUDE.md`), und dieses Paket gar nichts, und ein
 * ganzes Fachpaket in das Browser-Bündel zu ziehen, damit eine Rechtsseite eine
 * Vierzehn kennt, ist der Preis nicht wert.
 *
 * Sie abzuschreiben und sich darauf zu verlassen, wäre der übliche Fehler —
 * derselbe, der den Rücksetzlink im Entwurf von HM-059 auf 30 statt 60 Minuten
 * gebracht hat. Deshalb steht die Prüfung als **Test** daneben
 * (`legal.test.ts`): Er importiert beide Pakete, was ein Test darf, und schlägt
 * fehl, sobald eine Frist im Code sich ändert und diese Seite es nicht erfährt.
 * Ein roter Test ist die einzige Form von Erinnerung, die niemand übersieht.
 */

/** Wie lange ein Sicherheitscode gilt. Quelle: `EMAIL_CODE_LIFETIME_SECONDS`. */
export const CODE_LIFETIME_MINUTES = 10;
/** Wie viele Eingaben ein Vorgang verträgt. Quelle: `EMAIL_CODE_MAX_ATTEMPTS`. */
export const CODE_MAX_ATTEMPTS = 5;
/**
 * Wie lange eine unbestätigte Registrierung liegen bleibt.
 * Quelle: `PENDING_REGISTRATION_MAX_AGE_SECONDS`.
 *
 * **Der Entwurf nennt hier 30 Tage, und das wäre eine unrichtige Angabe.** Die
 * Zeile verschwindet nach 24 Stunden. Eine Datenschutzerklärung, die eine
 * längere Frist nennt als die tatsächliche, macht das Produkt schlechter, als
 * es ist — und eine, die eine kürzere nennt, wäre ein gebrochenes Versprechen.
 * Beides ist eine falsche Angabe nach Art. 13 Abs. 2 lit. a DSGVO.
 */
export const PENDING_REGISTRATION_MAX_AGE_HOURS = 24;

/**
 * Wie lange Rohmesswerte vorgehalten werden, in Tagen.
 * Quelle: `DEFAULT_RETENTION.raw`, letzte Stufe.
 *
 * **OQ-009 sagt ausdrücklich, dass diese Frist noch nicht gemessen ist** und
 * sich ändern wird. Genau deshalb hängt der Test daran: Wer sie nachjustiert,
 * bekommt hier einen Fehlschlag und nicht eine stillschweigend falsch gewordene
 * Datenschutzerklärung.
 */
export const RAW_RETENTION_DAYS = 14;

/**
 * Wie lange eine Sitzung ohne weitere Nutzung gilt (HM-101): ohne „Angemeldet
 * bleiben" in Stunden, mit Haken in Tagen. Beide Fristen sind **gleitend** —
 * jede Nutzung setzt sie neu (ADR 0015).
 *
 * Quelle: die Vorgabewerte `SESSION_TTL_SECONDS` und
 * `SESSION_REMEMBER_TTL_SECONDS` in `.env.example`, die auch der
 * Produktions-Compose setzt. Sie sind Betriebseinstellungen und nicht Domäne;
 * der Test liest deshalb die Datei. Wer sie auf dem Server abweichend setzt,
 * ändert diese Angaben mit — die Anmeldeseite zeigt dieselben Zahlen.
 */
export const SESSION_HOURS = 12;
export const SESSION_REMEMBER_DAYS = 90;

/**
 * Wie lange eine Einladung eines Bewohners gilt, in Tagen (HM-081).
 * Quelle: `INVITATION_LIFETIME_DAYS` in `@hausmeter/domain`.
 */
export const INVITATION_LIFETIME_DAYS = 14;

/**
 * Wie lange eine abgelaufene Zeile noch steht, bevor der Worker sie löscht, in
 * Tagen (OQ-046): Sitzungen, Einmal-Token, Kopplungsanfragen, Einladungen.
 * Vorher stand dort „bis zur Löschung des Kontos" — ehrlich, weil es keinen
 * Löschlauf gab. Quelle: `EXPIRED_RECORD_GRACE_DAYS` in `@hausmeter/domain`.
 */
export const EXPIRED_RECORD_GRACE_DAYS = 30;

/**
 * Wie lange ein Auftrag an ein Gerät aufbewahrt wird, in Tagen ab seinem
 * Entstehen (OQ-054) — etwa eine Betriebsart der Wallbox, die ein Bewohner
 * gesetzt hat. Vorher stand dort „bis zur Löschung des Kontos, dem das Gerät
 * gehört". Quelle: `DEVICE_COMMAND_RETENTION_DAYS` in `@hausmeter/domain`.
 */
export const DEVICE_COMMAND_RETENTION_DAYS = 90;

/**
 * Wie lange ein einzelnes Ereignis der Nutzungsstatistik mit seiner
 * Tageskennung aufbewahrt wird (HM-168, ADR 0046); die Tageszahlen daraus
 * tragen keine Kennung und bleiben. Quelle: `USAGE_EVENT_RETENTION_DAYS` in
 * `@hausmeter/domain`, dort zugleich die TTL von `usage_events`.
 */
export const USAGE_EVENT_RETENTION_DAYS = 90;

/**
 * Wie lange eine Sicherung aufbewahrt wird, in Tagen (HM-163). Mit dem Löschen
 * eines Kontos sind seine Daten sofort weg — aus den nächtlichen Sicherungen aber
 * erst, wenn die letzte, die sie enthält, überschrieben ist. Quelle: `KEEP_DAYS`
 * in `infra/scripts/backup.sh`.
 */
export const BACKUP_RETENTION_DAYS = 30;

/**
 * Welche Angaben fehlen — für den einen Hinweis, den eine Seite mit Lücken
 * bekommt. **Sie werden benannt und nicht gezählt:** „Einzelne Angaben fehlen"
 * lässt jemanden die Seite absuchen; „der Dienstleister für den E-Mail-Versand
 * fehlt" sagt, was zu tun ist.
 *
 * **Ein Hinweis und nicht vier** (die Lehre aus HM-041): Vier Zeilen
 * „nicht eingetragen" untereinander sehen aus wie ein Schaden. Die Zeilen
 * fehlen einfach, und ein Satz sagt, warum.
 */
export const OPEN_ENTRIES: readonly string[] = [
  ...(CONTACT.email === null ? ["eine E-Mail-Adresse für die Kontaktaufnahme"] : []),
  ...(CONTACT.phone === null ? ["eine Telefonnummer"] : []),
  ...(PROCESSORS_COMPLETE ? [] : ["der Betreiber des E-Mail-Servers"]),
  ...(EU_ONLY_CONFIRMED ? [] : ["der Standort des E-Mail-Servers"]),
];

export const HAS_OPEN_PLACEHOLDERS = OPEN_ENTRIES.length > 0;
